'use client'

import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowDown, ArrowUp, ArrowLeftRight, Check, Loader2 } from 'lucide-react'
import { formatMonthlyPlanPrice } from '@/lib/billing/utils/format'
import { PlanCardBody, PlanCreditsLine } from './plan-card-body'
import type { PublicPlan, BillingPlan } from '@/lib/billing/types'
import type { BillingChannel } from '@/lib/billing/constants'

/**
 * Minimal shape required to render a plan card. `PublicPlan` and `BillingPlan`
 * both satisfy this — `PublicPlan` is the registration/pricing-facing subset,
 * `BillingPlan` is the full DB row carried by tenant-billing-page. Listing
 * fields explicitly avoids tying this presentational component to whichever
 * type the caller happens to load.
 */
type PlanCardData = Pick<
  PublicPlan,
  | 'id'
  | 'name'
  | 'display_name'
  | 'price_monthly_cents'
  | 'expiry_days'
  | 'initial_balance_microusd'
  | 'payment_to_balance_microusd'
  | 'payment_to_balance_count'
  | 'plan_credits_expire'
  | 'status'
  | 'voice_included'
  | 'voice_trial_enabled'
  | 'voice_trial_duration_hours'
  | 'voice_agents_limit'
  | 'campaigns_enabled'
  | 'campaigns_limit'
  | 'pbx_numbers_limit'
  | 'knowledge_bases_limit'
  | 'knowledge_bases_size_mb_limit'
  | 'users_limit'
  | 'crm_integrations_limit'
  | 'shopify_stores_limit'
  | 'shopify_insights_trial_duration_hours'
  | 'marketing_channels_limit'
  | 'benefits'
>

export type PlanComparisonVariant = 'pricing' | 'register' | 'tenant-billing'

interface PlanComparisonCardProps {
  plan: PlanCardData | BillingPlan
  variant: PlanComparisonVariant
  channel: BillingChannel

  /** `register` variant only — selected card highlighting + click-to-select. */
  isSelected?: boolean
  onSelect?: (planId: string) => void

  /**
   * `tenant-billing` variant only — direction relative to the tenant's current
   * plan. Drives the CTA label and accent:
   *   - upgrade   → primary button
   *   - downgrade → outline button
   *   - lateral   → switch button (same price, different channel/cadence)
   *   - current   → disabled outline + "Current Plan" badge, no callback
   */
  direction?: 'upgrade' | 'downgrade' | 'lateral' | 'current'
  /** `tenant-billing` variant only — invoked on Upgrade / Downgrade click. */
  onChange?: (planId: string) => void
  /** `tenant-billing` variant only — disables CTA + shows spinner. */
  loading?: boolean
}

export function PlanComparisonCard({
  plan,
  isSelected,
  onSelect,
  variant,
  channel,
  direction,
  onChange,
  loading,
}: PlanComparisonCardProps) {
  const isFree = plan.price_monthly_cents === 0
  const isPublished = plan.status === 'published'
  // Selectable only when the caller wired a select handler. The Shopify register
  // flow renders the grid as a read-only showcase (no `onSelect`) — the merchant
  // picks a plan on Shopify, so these cards must NOT look clickable.
  const canSelect = variant === 'register' && isPublished && !!onSelect
  const isRegisterShowcase = variant === 'register' && !onSelect
  // Current-plan accent for the tenant-billing change-plan grid. The card
  // sits between downgrade and upgrade neighbours and needs a clear visual
  // anchor without being clickable.
  const isCurrent = variant === 'tenant-billing' && direction === 'current'

  return (
    <Card
      className={`relative flex flex-col transition-all ${
        canSelect ? 'cursor-pointer' : ''
      } ${
        isSelected
          ? 'border-blue-500 border-2 shadow-md'
          : isCurrent
            ? 'border-primary border-2 shadow-sm'
            : isRegisterShowcase
              ? 'group-hover:border-blue-300 group-hover:shadow-md'
              : 'hover:border-gray-300 hover:shadow-md'
      } ${
        !isPublished ? 'opacity-75' : ''
      }`}
      onClick={canSelect ? () => onSelect?.(plan.id) : undefined}
    >
      {isCurrent && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
          Current Plan
        </Badge>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{plan.display_name}</CardTitle>
          {variant === 'register' && isSelected && (
            <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
        <p className="text-3xl font-bold mt-1">
          {formatMonthlyPlanPrice(plan.price_monthly_cents)}
        </p>
        <PlanCreditsLine plan={plan} />
        {variant === 'register' && plan.expiry_days !== null && (
          <p className="text-sm text-muted-foreground">
            {`Lasts ${plan.expiry_days} ${plan.expiry_days === 1 ? 'day' : 'days'}`}
          </p>
        )}
      </CardHeader>

      <CardContent className="flex flex-col flex-1 gap-4">
        <PlanCardBody
          plan={plan}
        />

        {/* CTA — pricing variant: link to register flow */}
        {variant === 'pricing' && (
          isPublished ? (
            <Button asChild className="w-full mt-2" variant="outline">
              <Link href={`/auth/register?plan=${plan.name}&channel=${channel}`}>
                {isFree ? 'Get Started' : 'Sign Up'}
              </Link>
            </Button>
          ) : (
            <Button className="w-full mt-2" variant="outline" disabled>
              Coming Soon
            </Button>
          )
        )}

        {variant === 'register' && !isPublished && (
          <p className="text-center text-xs text-muted-foreground mt-2">Coming Soon</p>
        )}

        {/* CTA — tenant-billing variant: direction-aware Change Plan trigger.
            `current` renders a disabled stand-in so the card stays visually
            symmetric with its neighbours but communicates that there's
            nothing to switch to. */}
        {variant === 'tenant-billing' && direction === 'current' && (
          <Button
            className="w-full mt-2"
            size="sm"
            variant="outline"
            disabled
            aria-disabled="true"
          >
            <Check className="h-4 w-4 mr-1" />
            <span>Current Plan</span>
          </Button>
        )}
        {variant === 'tenant-billing' && direction && direction !== 'current' && (
          <Button
            className="w-full mt-2"
            size="sm"
            variant={direction === 'downgrade' ? 'outline' : 'default'}
            onClick={(e) => {
              e.stopPropagation()
              onChange?.(plan.id)
            }}
            disabled={loading || !isPublished}
          >
            {loading
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : direction === 'upgrade'
                ? <ArrowUp className="h-4 w-4 mr-1" />
                : direction === 'downgrade'
                  ? <ArrowDown className="h-4 w-4 mr-1" />
                  : <ArrowLeftRight className="h-4 w-4 mr-1" />}
            <span>{direction === 'upgrade' ? 'Upgrade' : direction === 'downgrade' ? 'Downgrade' : 'Switch'}</span>
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
